// Lazily-loaded wrapper around mixpanel-browser.
//
// mixpanel-browser is ~421 KiB (uncompressed) and was being statically
// imported into the main bundle even though analytics init is already
// deferred until idle/interaction (see runAnalyticsInitWhenIdle in
// src/index.js). This wrapper preserves the exact same call sites
// (mixpanel.init/track/identify/people.set/etc.) used across index.js and
// custom-analytics.js, but loads the real library via dynamic import() the
// first time any method is invoked, queuing calls made before it resolves
// and flushing them in order once it's ready.
//
// Prerender safety: react-snap (ReactSnap UA) must never trigger the
// dynamic import - the same IS_PRERENDER detection used in index.js is
// duplicated here so this module is safe to import from anywhere without
// relying on call sites to gate it themselves.
import { startRecordingForCurrentSession } from './productAnalyticsSession';
import { installMixpanelReplayGuard } from './mixpanelReplayGuard';

const IS_PRERENDER =
    typeof navigator !== 'undefined' &&
    (
        navigator.userAgent === 'ReactSnap' ||
        navigator.userAgent.includes('ReactSnap') ||
        (typeof window !== 'undefined' && typeof window.snapSaveState === 'function')
    );

let instance = null;
let loadPromise = null;
let initialized = false;
let replayOperations = Promise.resolve();
let replayPredicate = () => false;
const queue = [];

const flushQueue = () => {
    // mixpanel-browser creates its config and hook state in init(). Loading
    // the module alone is not enough to safely call track/identify/people.
    if (!instance || !initialized) {
        return;
    }

    const pending = queue.splice(0, queue.length);
    pending.forEach(fn => {
        try {
            fn(instance);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Mixpanel queued call failed:', error);
            }
        }
    });
};

const ensureLoaded = () => {
    if (IS_PRERENDER) {
        // Never fetch the chunk during static prerendering. Calls just stay
        // queued forever (harmless - prerender never fires real tracking).
        return Promise.resolve(null);
    }

    if (!loadPromise) {
        loadPromise = import('mixpanel-browser')
            .then(mod => {
                instance = mod.default || mod;
                return instance;
            })
            .catch(error => {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Failed to load mixpanel-browser:', error);
                }
                return null;
            });
    }
    return loadPromise;
};

// Fire-and-forget call: queues until the lib is ready, then invokes `fn(mp)`.
const withMixpanel = (fn) => {
    if (instance && initialized) {
        fn(instance);
        return;
    }
    queue.push(fn);
    ensureLoaded();
};

const initialize = (isCurrent, ...args) => {
    replayPredicate = isCurrent;
    if (initialized) {
        return;
    }

    ensureLoaded().then(mp => {
        if (!mp || initialized || !isCurrent()) {
            return;
        }

        try {
            const [token, config = {}, ...rest] = args;
            mp.init(token, {
                ...config,
                loaded: sdk => {
                    installMixpanelReplayGuard(sdk, () => replayPredicate);
                    config.loaded?.(sdk);
                }
            }, ...rest);
            initialized = true;
            flushQueue();
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Failed to initialize Mixpanel:', error);
            }
        }
    });
};

// Awaitable call: resolves the lib then invokes `fn(mp)`, returning its result.
const withMixpanelAsync = async (fn) => {
    const mp = instance || (await ensureLoaded());
    if (!mp || !initialized) {
        return null;
    }
    return fn(mp);
};

const mixpanelClient = {
    // Kick off (or continue) loading the real library without queuing a call.
    ensureLoaded,

    // Initialization is a barrier: calls that arrive while the library is
    // loading must wait until init has established Mixpanel's config.
    init: (...args) => initialize(() => true, ...args),
    init_if: (isCurrent, ...args) => initialize(isCurrent, ...args),
    register: (...args) => withMixpanel(mp => mp.register(...args)),
    track: (...args) => withMixpanel(mp => mp.track(...args)),
    identify: (...args) => withMixpanel(mp => mp.identify(...args)),
    alias: (...args) => withMixpanel(mp => mp.alias(...args)),
    reset: (...args) => withMixpanel(mp => mp.reset(...args)),
    opt_in_tracking: (...args) => withMixpanel(mp => mp.opt_in_tracking(...args)),
    opt_out_tracking: (...args) => withMixpanel(mp => mp.opt_out_tracking(...args)),
    start_session_recording: (...args) => withMixpanel(mp => {
        if (typeof mp.start_session_recording === 'function') {
            mp.start_session_recording(...args);
        }
    }),
    start_session_recording_if: isCurrent => withMixpanel(mp => {
        replayPredicate = isCurrent;
        if (typeof mp.start_session_recording === 'function') {
            replayOperations = replayOperations.then(() => startRecordingForCurrentSession(mp, isCurrent)).catch(() => {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Mixpanel session replay start failed');
                }
            });
        }
    }),
    stop_session_recording: (...args) => {
        replayPredicate = () => false;
        // Excluding an admin or anonymous visitor must not load the SDK.
        if (!instance && !loadPromise) return;
        withMixpanel(mp => {
            if (typeof mp.stop_session_recording === 'function') {
                mp.stop_session_recording(...args);
            }
        });
    },

    // Synchronous getter used inside hot-path checks (e.g. safeMixpanelCall).
    // Before the lib has loaded there's nothing opted-out yet, so `false` is
    // the correct/safe answer (tracking calls get queued rather than
    // dropped).
    has_opted_out_tracking: () => (
        instance && initialized ? instance.has_opted_out_tracking() : false
    ),

    // These two are always awaited at call sites (custom-analytics.js), so
    // they can safely be promise-returning even though mixpanel-browser's
    // real API is synchronous.
    get_distinct_id: () => withMixpanelAsync(mp => mp.get_distinct_id()),
    get_property: (prop) => withMixpanelAsync(mp => mp.get_property(prop)),

    people: {
        set: (...args) => withMixpanel(mp => mp.people.set(...args)),
        increment: (...args) => withMixpanel(mp => mp.people.increment(...args))
    }
};

export default mixpanelClient;
